import React, { useEffect, useMemo, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { BodyFigure, type BodySide } from './BodyFigure';
import { SkillTrace } from './SkillTrace';
import { outcomeOf } from './skillScenario';
import { Spinner } from './Spinner';
import {
  SkillMapService,
  STAGES,
  type MappedSkill,
  type SkillMap,
} from '../services/skillMapService';
import '../styles/prompt-rules-panel.css';
import '../styles/skill-body-map.css';

// Skill Manager Body Map (AXISREF497, AXISREF498): browse existing skills by the body part
// they apply to. Skills with no body part are everyday skills and are parked separately.

// Axis light blue-grey family; vibrant orange is reserved for the selected body part.
const LOW = '#d3dfe1';
const HIGH = '#4f6a6a';
const SELECTED = '#e84b37';

function mix(a: string, b: string, t: number): string {
  const parse = (h: string) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const [x, y] = [parse(a), parse(b)];
  return '#' + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, '0')).join('');
}

function kindLabel(skill: MappedSkill): string {
  return skill.kind === 'red_flag' ? 'Red flag' : 'Skill rule';
}

function stageLabel(stage: string): string {
  return STAGES.find(s => s.id === stage)?.label ?? stage;
}

function whyTagged(skill: MappedSkill, regionCode: string): string | null {
  const tag = skill.regions.find(r => r.code === regionCode);
  if (!tag) return null;
  if (tag.source === 'manual') return 'Tagged by an author';
  return tag.matched_term ? `Found "${tag.matched_term}" in its description` : 'Found in its description';
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`status-badge badge-${status}`}>{status}</span>
);

const SkillRow: React.FC<{ skill: MappedSkill; regionCode: string }> = ({ skill, regionCode }) => {
  const why = whyTagged(skill, regionCode);
  return (
    <li className="body-map-skill" data-testid="body-map-skill">
      <div className="body-map-skill-top">
        <div>
          <div className="body-map-skill-name">{skill.name}</div>
          <div className="body-map-skill-meta">
            {kindLabel(skill)} · {stageLabel(skill.stage)}
            {why && <> · {why}</>}
          </div>
        </div>
        <StatusBadge status={skill.status} />
      </div>
      {skill.description && (
        <details className="body-map-skill-detail">
          <summary>
            <FeatherIcon icon="chevron-right" size={14} className="body-map-chevron" />
            {skill.kind === 'red_flag' ? 'Description' : 'Rule text'}
          </summary>
          <p>{skill.description}</p>
        </details>
      )}
    </li>
  );
};

// AXISREF499: urgency (Decide urgency stage) skills that can apply to the selected body part,
// revealed only when the author asks for them.
// Small CTA on each urgency row: where the skill leads, and the way into its trace.
const TracePill: React.FC<{ skill: MappedSkill; outcome: string | null; active: boolean; onTrace: () => void }> = ({
  skill, outcome, active, onTrace,
}) => (
  <button
    type="button"
    className={`body-map-trace-pill${active ? ' active' : ''}`}
    aria-pressed={active}
    aria-label={`Trace ${skill.name}${outcome ? ` to ${outcome}` : ''}`}
    onClick={onTrace}
  >
    {outcome ?? 'Trace'}
    <FeatherIcon icon="arrow-right" size={12} />
  </button>
);

const UrgencySection: React.FC<{
  regionCode: string;
  regionName: string;
  specific: MappedSkill[];
  everyday: MappedSkill[];
  open: boolean;
  onToggle: () => void;
  outcomeFor: (skill: MappedSkill) => string | null;
  tracedId: string | null;
  onTrace: (skill: MappedSkill) => void;
}> = ({ regionCode, regionName, specific, everyday, open, onToggle, outcomeFor, tracedId, onTrace }) => {
  const redFlags = everyday.filter(s => s.kind === 'red_flag');
  const rules = everyday.filter(s => s.kind !== 'red_flag');
  const panelId = 'body-map-urgency-panel';
  const pill = (skill: MappedSkill) => (
    <span className="body-map-urgency-actions">
      <StatusBadge status={skill.status} />
      <TracePill
        skill={skill}
        outcome={outcomeFor(skill)}
        active={tracedId === `${skill.kind}-${skill.id}`}
        onTrace={() => onTrace(skill)}
      />
    </span>
  );

  return (
    <div className="body-map-urgency">
      <button
        type="button"
        className="body-map-urgency-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <FeatherIcon icon="chevron-right" size={16} className="body-map-chevron" />
        <span className="body-map-urgency-title">Decide urgency</span>
        <span className="body-map-urgency-count">
          {specific.length} for the {regionName} · {everyday.length} for every referral
        </span>
      </button>

      {open && (
        <div id={panelId} className="body-map-urgency-panel" data-testid="body-map-urgency">
          <section aria-label={`Urgency skills written for the ${regionName}`}>
            <h3>
              Written for the {regionName} <span>{specific.length}</span>
            </h3>
            {specific.length ? (
              <ul className="body-map-urgency-list">
                {specific.map(skill => (
                  <li key={`${skill.kind}-${skill.id}`} className="body-map-urgency-row">
                    <span>
                      <span className="body-map-urgency-name">{skill.name}</span>
                      <span className="body-map-urgency-meta">
                        {kindLabel(skill)}
                        {whyTagged(skill, regionCode) && <> · {whyTagged(skill, regionCode)}</>}
                      </span>
                    </span>
                    {pill(skill)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="body-map-sub">No urgency skills are written for the {regionName} yet.</p>
            )}
          </section>

          <section aria-label="Urgency skills that apply to every referral">
            <h3>
              Every referral <span>{everyday.length}</span>
            </h3>
            {[
              { label: 'Red flags', list: redFlags },
              { label: 'Triage rules', list: rules },
            ]
              .filter(group => group.list.length)
              .map(group => (
                <div key={group.label} className="body-map-urgency-group">
                  <h4>
                    {group.label} <span>{group.list.length}</span>
                  </h4>
                  <ul className="body-map-urgency-list">
                    {group.list.map(skill => (
                      <li key={`${skill.kind}-${skill.id}`} className="body-map-urgency-row">
                        <span className="body-map-urgency-name">{skill.name}</span>
                        {pill(skill)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </section>
        </div>
      )}
    </div>
  );
};

export const SkillBodyMap: React.FC = () => {
  const [data, setData] = useState<SkillMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedSide, setSelectedSide] = useState<BodySide>('na');
  const [hover, setHover] = useState<{ region: string; side: BodySide; x: number; y: number } | null>(null);
  const [urgencyOpen, setUrgencyOpen] = useState(false);
  // AXISREF499: the chain of urgency skills being traced from the selected body part.
  const [chain, setChain] = useState<MappedSkill[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await SkillMapService.getSkillMap());
    } catch (err: any) {
      console.error('Failed to load skill map:', err);
      setError('Failed to load the body map. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { bySkillRegion, everyday, maxCount } = useMemo(() => {
    const byRegion: Record<string, MappedSkill[]> = {};
    const daily: MappedSkill[] = [];
    for (const skill of data?.skills ?? []) {
      if (!skill.regions.length) {
        daily.push(skill);
        continue;
      }
      for (const tag of skill.regions) {
        (byRegion[tag.code] = byRegion[tag.code] || []).push(skill);
      }
    }
    const max = Math.max(1, ...Object.values(byRegion).map(list => list.length));
    return { bySkillRegion: byRegion, everyday: daily, maxCount: max };
  }, [data]);

  const regions = data?.regions ?? [];
  const regionLabel = (code: string) => regions.find(r => r.code === code)?.label ?? code;
  const countFor = (code: string) => bySkillRegion[code]?.length ?? 0;

  const fillFor = (code: string) => {
    const n = countFor(code);
    if (selected === code) return SELECTED;
    if (!n) return 'url(#body-map-hatch)';
    return mix(LOW, HIGH, 0.15 + (0.85 * n) / maxCount);
  };

  const select = (code: string | null, side: BodySide = 'na') => {
    if (code !== selected) {
      setUrgencyOpen(false);
      setChain([]);
    }
    setSelected(code);
    setSelectedSide(side);
  };

  if (loading && !data) {
    return (
      <div className="loading-state">
        <Spinner size="md" />
        <p>Loading body map...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="error-banner">
        <span>{error}</span>
        <button className="btn btn-link" onClick={load}>Retry</button>
      </div>
    );
  }


  const ranked = [...regions].sort((a, b) => countFor(b.code) - countFor(a.code) || a.sort_order - b.sort_order);
  const selectedSkills = selected ? bySkillRegion[selected] ?? [] : [];
  const urgencySpecific = selectedSkills.filter(s => s.stage === 'triage');
  const urgencyEveryday = everyday.filter(s => s.stage === 'triage');
  const everydayByStage = STAGES.map(stage => ({
    stage,
    skills: everyday.filter(s => s.stage === stage.id),
  })).filter(group => group.skills.length);

  const tracing = !!selected && chain.length > 0;
  const tracedId = tracing ? `${chain[0].kind}-${chain[0].id}` : null;
  const allSkills = data?.skills ?? [];

  return (
    <div className={`body-map${tracing ? ' tracing' : ''}`} data-testid="skill-body-map">
      <svg width="0" height="0" className="body-map-defs" aria-hidden="true">
        <defs>
          <pattern id="body-map-hatch" patternUnits="userSpaceOnUse" width="3" height="3" patternTransform="rotate(45)">
            <rect width="3" height="3" fill="#eef2f3" />
            <line x1="0" y1="0" x2="0" y2="3" stroke="#c3cbd1" strokeWidth="1" />
          </pattern>
        </defs>
      </svg>

      <div className="body-map-grid">
        <section className="body-map-card body-map-figure-card" aria-label="Body figure">
          <div className="body-map-figures">
            {(['front', 'back'] as const).map(view => (
              <figure className="body-map-figure" key={view}>
                <BodyFigure
                  view={view}
                  fillFor={fillFor}
                  labelFor={code => `${regionLabel(code)}: ${countFor(code)} body-specific skills`}
                  selectedRegion={selected}
                  selectedSide={selectedSide}
                  onSelect={select}
                  onHover={(region, side, e) =>
                    setHover(region && e ? { region, side, x: e.clientX, y: e.clientY } : null)
                  }
                />
                <figcaption>{view === 'front' ? 'Front' : 'Back'}</figcaption>
              </figure>
            ))}
          </div>
          <div className="body-map-legend">
            <span>
              Fewer
              <span className="body-map-scale" style={{ background: `linear-gradient(90deg, ${mix(LOW, HIGH, 0.15)}, ${HIGH})` }} />
              More body-specific skills (up to {maxCount})
            </span>
            <span className="body-map-gapkey">
              <i /> None yet
            </span>
          </div>
        </section>

        <section className="body-map-card body-map-region-card">
          <div className="body-map-region-main" aria-live="polite">
          {!selected ? (
            <>
              <div>
                <h2>Pick a body part</h2>
                <p className="body-map-sub">Click the body or a row below to see the skills written for it.</p>
              </div>
              <ul className="body-map-region-list">
                {ranked.map(region => {
                  const n = countFor(region.code);
                  return (
                    <li key={region.code}>
                      <button className="body-map-region-row" onClick={() => select(region.code)}>
                        <span>{region.label}</span>
                        <span className={`body-map-count${n ? '' : ' zero'}`}>
                          {n ? `${n} skill${n === 1 ? '' : 's'}` : 'none yet'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <>
              <div>
                <h2>
                  <span className="body-map-region-title">{regionLabel(selected)}</span>
                  {selectedSide !== 'na' && <span className="body-map-side"> · {selectedSide}</span>}
                </h2>
                <p className="body-map-sub">
                  {selectedSkills.length
                    ? `${selectedSkills.length} skill${selectedSkills.length === 1 ? '' : 's'} written for this body part, plus ${everyday.length} everyday skills.`
                    : `No skills mention this body part yet. Its referrals rely on the ${everyday.length} everyday skills.`}{' '}
                  Body side is recorded separately; the same skills apply to left and right.
                </p>
              </div>
              {selectedSkills.length > 0 && (
                <ul className="body-map-skill-list">
                  {selectedSkills.map(skill => (
                    <SkillRow key={`${skill.kind}-${skill.id}`} skill={skill} regionCode={selected} />
                  ))}
                </ul>
              )}
              {urgencySpecific.length + urgencyEveryday.length > 0 && (
                <UrgencySection
                  regionCode={selected}
                  regionName={regionLabel(selected).toLowerCase()}
                  specific={urgencySpecific}
                  everyday={urgencyEveryday}
                  open={urgencyOpen}
                  onToggle={() => {
                    setUrgencyOpen(open => !open);
                    setChain([]);
                  }}
                  outcomeFor={skill => outcomeOf(allSkills, selected, skill)}
                  tracedId={tracedId}
                  onTrace={skill => setChain(prev => (prev[0]?.id === skill.id && prev[0]?.kind === skill.kind ? [] : [skill]))}
                />
              )}
              <button className="btn btn-link body-map-back" onClick={() => select(null)}>
                <FeatherIcon icon="arrow-left" size={14} />
                All body parts
              </button>
            </>
          )}
          </div>
          {tracing && (
            <SkillTrace
              skills={allSkills}
              region={selected!}
              regionName={regionLabel(selected!)}
              chain={chain}
              onFollow={skill =>
                setChain(prev => {
                  const seen = prev.findIndex(s => s.id === skill.id && s.kind === skill.kind);
                  return seen >= 0 ? prev.slice(0, seen + 1) : [...prev, skill];
                })
              }
              onBackTo={index => setChain(prev => prev.slice(0, index + 1))}
              onClose={() => setChain([])}
            />
          )}
        </section>
      </div>

      <details className="body-map-card body-map-everyday" data-testid="body-map-everyday">
        <summary>
          <FeatherIcon icon="chevron-right" size={16} className="body-map-chevron" />
          <span>
            <strong>Everyday skills</strong>
            <span className="body-map-sub"> · {everyday.length} run on every referral, whatever the body part</span>
          </span>
        </summary>
        <div className="body-map-everyday-groups">
          {everydayByStage.map(group => (
            <div className="body-map-everyday-group" key={group.stage.id}>
              <h3>
                {group.stage.label} <span>{group.skills.length}</span>
              </h3>
              <ul>
                {group.skills.map(skill => (
                  <li key={`${skill.kind}-${skill.id}`}>
                    <span className="body-map-everyday-name">{skill.name}</span>
                    <span className="body-map-everyday-kind">{kindLabel(skill)}</span>
                    <StatusBadge status={skill.status} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </details>

      {hover && (
        <div className="body-map-tooltip" style={{ left: hover.x + 14, top: hover.y + 14 }} role="tooltip">
          <strong>
            {regionLabel(hover.region)}
            {hover.side !== 'na' ? ` · ${hover.side}` : ''}
          </strong>
          {countFor(hover.region)} body-specific skill{countFor(hover.region) === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
};
